import { useEffect, useState } from "react";
import { useTranslation } from "@lib/i18n";

import {
  lazyLoadGoogleMaps,
  lazyLoadMarkerClusterer,
} from "@shared/v2/widgets/googleMapsWidget";

const each = (items, fn) => {
  const normalizedItems = Array.isArray(items) ? items : [items];

  normalizedItems.forEach(fn);
};

const smoothZoom = map => (targetZoom, nextZoom = null) => {
  const DELAY = 80;
  const currentZoom = nextZoom ?? map.getZoom();

  if (currentZoom === targetZoom) {
    setTimeout(() => map.setZoom(currentZoom), DELAY);

    return;
  }

  window.google.maps.event.addListenerOnce(map, "zoom_changed", () => {
    smoothZoom(map)(
      targetZoom,
      currentZoom + (targetZoom > currentZoom ? 1 : -1)
    );
  });

  setTimeout(() => map.setZoom(currentZoom), DELAY);
};

const render = map => items => each(items, item => item.setMap(map));

const remove = items => each(items, item => item.setMap(null));

const createMarker = options => new window.google.maps.Marker(options);

const createCircle = options => new window.google.maps.Circle(options);

const addClusterer = map => (markers, options) =>
  new window.MarkerClusterer(map, markers, options);

export const useMap = ({ containerRef, options }) => {
  const [mapInstance, setMapInstance] = useState(null);
  const {
    i18n: { language },
  } = useTranslation();

  useEffect(() => {
    Promise.all([lazyLoadGoogleMaps(language), lazyLoadMarkerClusterer()]).then(
      ([maps]) => {
        const map = new maps.Map(containerRef.current, options);
        setMapInstance({
          addClusterer: addClusterer(map),
          render: render(map),
          smoothZoom: smoothZoom(map),

          instance: map,

          maps,
          createMarker,
          createCircle,
          remove,
        });
      }
    );
  }, [containerRef.current, language]);

  return mapInstance;
};
